// src/components/author/ManuscriptsWithDecisions.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
  RefreshCw,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  MessageSquare,
  Edit,
  Users,
  Tag,
  ArrowLeft,
  ArrowRight,
  FileEdit,
  Mail,
  Loader,
  Hash,
  Download,
  File as FileIcon,
  FileSpreadsheet,
  Image as ImageIcon,
  FileArchive
} from 'lucide-react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/toast';
import RichTextViewer from '../common/RichTextViewer';
import ManuscriptActions from './ManuscriptActions';
import {
  formatStatus,
  formatDate,
  getStatusClass,
  getStatusIcon,
  StatusBadge,
  handleDownloadFile
} from '../../utils/utils.global';

// ---------------------------------------------------------
// File list helpers (shared shape with ManuscriptsList)
// ---------------------------------------------------------

const FILE_LABELS = {
  manuscript_file: 'Manuscript',
  cover_letter_file: 'Cover Letter',
  tables: 'Tables',
  figures: 'Figures',
  supplementary_material: 'Supplementary Material',
  tracked_manuscript_file: 'Manuscript with Tracked Changes',
  graphic_abstract: 'Graphical Abstract',
  manuscriptPhoto: 'Cover Image',
  document_file: 'Document'
};

const getManuscriptFiles = (manuscript) => {
  if (!manuscript) return [];

  const source =
    manuscript.files && typeof manuscript.files === 'object'
      ? manuscript.files
      : manuscript;

  const seen = new Set();
  const files = [];

  const pushFile = (key, url) => {
    if (!url || typeof url !== 'string') return;
    const normalized = url.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    files.push({
      key,
      label: FILE_LABELS[key] || key.replace(/_/g, ' '),
      url: normalized
    });
  };

  Object.keys(FILE_LABELS).forEach((key) => {
    if (source[key]) pushFile(key, source[key]);
  });

  Object.entries(source).forEach(([key, value]) => {
    if (FILE_LABELS[key]) return;
    if (!/file|url/i.test(key)) return;
    if (typeof value !== 'string') return;
    pushFile(key, value);
  });

  return files;
};

const getFileIcon = (url) => {
  const ext = String(url).split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageIcon;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['zip', 'rar', '7z'].includes(ext)) return FileArchive;
  if (['pdf', 'doc', 'docx'].includes(ext)) return FileText;
  return FileIcon;
};

const displayFilename = (url) => {
  try {
    const name = decodeURIComponent(String(url).split('/').pop() || '');
    return name || 'file';
  } catch {
    return String(url).split('/').pop() || 'file';
  }
};

// ---------------------------------------------------------
// Component
// ---------------------------------------------------------

const ManuscriptsWithDecisions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const urlManuscriptId = queryParams.get('a');
  const urlPage = parseInt(queryParams.get('page')) || 1;
  const urlSearch = queryParams.get('search') || '';
  const urlDecision = queryParams.get('decision') || 'all';

  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [manuscripts, setManuscripts] = useState([]);
  const [stats, setStats] = useState({
    accepted: 0,
    rejected: 0,
    revisions: 0,
    total: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: urlPage,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });

  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(urlSearch);
  const [decisionFilter, setDecisionFilter] = useState(urlDecision);
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('decisions_view');
    return saved || 'table';
  });

  const [selectedManuscript, setSelectedManuscript] = useState(null);
  const [selectedManuscriptDetails, setSelectedManuscriptDetails] = useState(null);
  const [selectedManuscriptIndex, setSelectedManuscriptIndex] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [relatedSubmissions, setRelatedSubmissions] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  // ---------------------------------------------------------
  // Effects
  // ---------------------------------------------------------

  useEffect(() => {
    localStorage.setItem('decisions_view', viewType);
  }, [viewType]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchManuscripts();
  }, [pagination.currentPage, decisionFilter, debouncedSearchTerm]);

  useEffect(() => {
    if (urlDecision) setDecisionFilter(urlDecision);
    if (urlSearch) setSearchTerm(urlSearch);
  }, [urlDecision, urlSearch]);

  // Auto-open from `?a=`
  useEffect(() => {
    if (urlManuscriptId && manuscripts.length > 0) {
      const idx = manuscripts.findIndex(
        (m) => (m.revision_id || m.id) === urlManuscriptId
      );
      if (idx !== -1) {
        openManuscript(manuscripts[idx], idx);
      }
    }
    // eslint-disable-next-line
  }, [urlManuscriptId, manuscripts]);

  // ---------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------

  const fetchManuscripts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/authors/manuscripts/decisions', {
        params: {
          page: pagination.currentPage,
          limit: pagination.limit,
          search: debouncedSearchTerm,
          decision: decisionFilter,
          sortBy: 'id',
          sortOrder: 'DESC'
        }
      });

      if (response.data.status === 'success') {
        setManuscripts(response.data.manuscripts);
        setPagination(response.data.pagination);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching manuscripts:', err);
      showToast('Failed to load manuscripts. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedSubmissions = async (manuscriptId) => {
    if (!manuscriptId) return;
    setLoadingRelated(true);
    try {
      const response = await axios.get(
        `/api/authors/submissions/${manuscriptId}/related`
      );
      if (response.data.status === 'success') {
        setRelatedSubmissions(response.data.submissions || []);
      } else {
        setRelatedSubmissions([]);
      }
    } catch (err) {
      console.error('Error fetching related submissions:', err);
      setRelatedSubmissions([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  const fetchDecisionLetter = async (manuscript) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(
        `/api/authors/manuscripts/${manuscript.revision_id || manuscript.id}/decision-letter`
      );

      if (response.data.status === 'success') {
        setSelectedManuscriptDetails({
          ...manuscript,
          ...response.data.submission,
          decisionLetter: response.data.decisionLetter,
          reviewerComments: response.data.reviewerComments || [],
          revisionsDue: response.data.revisionsDue
        });
      } else {
        setSelectedManuscriptDetails({
          ...manuscript,
          reviewerComments: [],
          decisionLetter: null,
          revisionsDue: null
        });
      }
    } catch (err) {
      console.error('Error fetching decision letter:', err);
      setSelectedManuscriptDetails({
        ...manuscript,
        reviewerComments: [],
        decisionLetter: null,
        revisionsDue: null
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  // ---------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------

  const buildDecisionsUrl = (overrides = {}) => {
    const params = new URLSearchParams();
    const page = overrides.page ?? pagination.currentPage;
    const search = overrides.search ?? searchTerm;
    const decision = overrides.decision ?? decisionFilter;
    const a = overrides.a;

    if (a) params.set('a', a);
    params.set('page', String(page));
    if (search) params.set('search', search);
    if (decision && decision !== 'all') params.set('decision', decision);

    return `/inreview?${params.toString()}`;
  };

  const handleContinueSubmission = (manuscriptId, subtype = 'new') => {
    if (subtype === 'correction') {
      navigate(`/submit?a=${manuscriptId}&status=correction&correction=true`);
    } else if (subtype === 'revision') {
      navigate(`/submit?a=${manuscriptId}&status=revision&revision=true`);
    } else {
      navigate(`/submit?a=${manuscriptId}&status=${subtype}`);
    }
  };



  const getDecisionColor = (status) => {
    if (status === 'accepted') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'rejected') return 'bg-red-100 text-red-800 border-red-200';
    if (status === 'returned_for_revision' || status === 'returned_for_correction')
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getDecisionIcon = (status) => {
    if (status === 'accepted') return <CheckCircle className="w-4 h-4 mr-1" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4 mr-1" />;
    if (status === 'returned_for_revision') return <Edit className="w-4 h-4 mr-1" />;
    if (status === 'returned_for_correction') return <AlertCircle className="w-4 h-4 mr-1" />;
    return null;
  };

  const getDecisionText = (status) => {
    if (status === 'accepted') return 'Accepted';
    if (status === 'rejected') return 'Rejected';
    if (status === 'returned_for_revision') return 'Revision Required';
    if (status === 'returned_for_correction') return 'Correction Required';
    return status;
  };

  // ---------------------------------------------------------
  // Modal handlers
  // ---------------------------------------------------------

  const openManuscript = async (manuscript, index) => {
    setSelectedManuscript(manuscript);
    setSelectedManuscriptIndex(index);
    setShowViewModal(true);
    fetchRelatedSubmissions(manuscript.revision_id || manuscript.id);
    await fetchDecisionLetter(manuscript);
    navigate(buildDecisionsUrl({ a: manuscript.revision_id || manuscript.id }), {
      replace: true
    });
  };

  const handleViewManuscript = (manuscript, index) => {
    const absoluteIndex = (pagination.currentPage - 1) * pagination.limit + index;
    openManuscript(manuscript, absoluteIndex);
  };

  const handleSelectRelatedManuscript = async (submission) => {
    const manuscriptIndex = manuscripts.findIndex(
      (m) => (m.revision_id || m.id) === (submission.revision_id || submission.id)
    );
    setSelectedManuscript(submission);
    setSelectedManuscriptIndex(manuscriptIndex !== -1 ? manuscriptIndex : null);
    setRelatedSubmissions([]);
    fetchRelatedSubmissions(submission.revision_id || submission.id);
    await fetchDecisionLetter(submission);
    navigate(buildDecisionsUrl({ a: submission.revision_id || submission.id }), {
      replace: true
    });
  };

  const handleNavigateManuscript = async (direction) => {
    if (selectedManuscriptIndex === null) return;
    const newIndex = selectedManuscriptIndex + direction;
    if (newIndex < 0 || newIndex >= pagination.totalCount) return;

    const pageIndex = newIndex % pagination.limit;
    const pageNum = Math.floor(newIndex / pagination.limit) + 1;

    if (pageNum !== pagination.currentPage) {
      setPagination((prev) => ({ ...prev, currentPage: pageNum }));
      // Let the fetch complete; the auto-open effect will handle the modal
      return;
    }

    const newManuscript = manuscripts[pageIndex];
    if (!newManuscript) return;

    setSelectedManuscript(newManuscript);
    setSelectedManuscriptIndex(newIndex);
    setRelatedSubmissions([]);
    fetchRelatedSubmissions(newManuscript.revision_id || newManuscript.id);
    await fetchDecisionLetter(newManuscript);
    navigate(buildDecisionsUrl({ a: newManuscript.revision_id || newManuscript.id }), {
      replace: true
    });
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedManuscript(null);
    setSelectedManuscriptDetails(null);
    setSelectedManuscriptIndex(null);
    setRelatedSubmissions([]);
    navigate(buildDecisionsUrl(), { replace: true });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
      navigate(buildDecisionsUrl({ page }), { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate(buildDecisionsUrl({ page: 1, search: value }), { replace: true });
  };

  const handleDecisionFilterChange = (e) => {
    const value = e.target.value;
    setDecisionFilter(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate(buildDecisionsUrl({ page: 1, decision: value }), { replace: true });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDecisionFilter('all');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate('/inreview', { replace: true });
  };

  const handleRefresh = () => fetchManuscripts();

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  if (loading && manuscripts.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading manuscripts with decisions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[--asfi-blue] flex items-center">
              <FileEdit className="w-6 h-6 mr-3" />
              Manuscripts With Decisions
            </h1>
            <p className="text-gray-600 mt-2">
              View manuscripts that have received editorial decisions and reviewer feedback
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Decisions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileEdit className="w-5 h-5 text-[--asfi-blue]" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Accepted</p>
                  <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Revisions</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.revisions}</p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Edit className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Total: <span className="font-medium">{pagination.totalCount}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">
                  Showing:{' '}
                  <span className="font-medium">
                    {(pagination.currentPage - 1) * pagination.limit + 1}-
                    {Math.min(
                      pagination.currentPage * pagination.limit,
                      pagination.totalCount
                    )}
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewType('table')}
                    className={`p-2 rounded-md transition-all ${
                      viewType === 'table'
                        ? 'bg-white shadow-sm text-[--asfi-blue]'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Table View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewType('grid')}
                    className={`p-2 rounded-md transition-all ${
                      viewType === 'grid'
                        ? 'bg-white shadow-sm text-[--asfi-blue]'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title or manuscript ID..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <select
                  value={decisionFilter}
                  onChange={handleDecisionFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Decisions</option>
                  <option value="accept">Accepted</option>
                  <option value="revision">Revisions</option>
                  <option value="reject">Rejected</option>
                </select>
              </div>
            </div>

            {(searchTerm || decisionFilter !== 'all') && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="text-sm text-[--asfi-blue] hover:text-purple-800 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Manuscripts Display */}
          {manuscripts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <FileEdit className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No decisions found
              </h3>
              <p className="text-gray-500">
                {searchTerm || decisionFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : "You don't have any manuscripts with editorial decisions yet."}
              </p>
              {(searchTerm || decisionFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-[--asfi-blue] hover:text-purple-800"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : viewType === 'table' ? (
            /* Table View */
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[--asfi-blue]">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Decision
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Decision Date
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {manuscripts.map((manuscript, index) => (
                      <tr
                        key={manuscript.revision_id || manuscript.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getDecisionColor(
                              manuscript.status
                            )}`}
                          >
                            {getDecisionIcon(manuscript.status)}
                            {getDecisionText(manuscript.status)}
                          </span>
                          <StatusBadge status={manuscript.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {manuscript.revision_id || manuscript.id}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {manuscript.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {manuscript.type || manuscript.article_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(
                            manuscript.lastUpdated || manuscript.last_updated
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewManuscript(manuscript, index)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                              title="View Decision & Feedback"
                            >
                              View Decision
                            </button>

                            <ManuscriptActions
                              submission={manuscript}
                              navigate={navigate}
                              onContinue={handleContinueSubmission}
                              size="sm"
                              variant="buttons"
                              emptyFallback={null}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {manuscripts.map((manuscript, index) => (
                <div
                  key={manuscript.revision_id || manuscript.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[--asfi-blue] mb-1">
                          {manuscript.revision_id || manuscript.id}
                        </p>
                        <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-2">
                          {manuscript.title}
                        </h3>
                      </div>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getDecisionColor(
                          manuscript.status
                        )}`}
                      >
                        {getDecisionIcon(manuscript.status)}
                        {getDecisionText(manuscript.status)}
                      </span>
                    </div>

                    <div className="flex items-center mb-3">
                      <Tag className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        {manuscript.type || manuscript.article_type}
                      </span>
                    </div>

                    <div className="flex items-center mb-4">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-600">
                        Decision:{' '}
                        {formatDate(
                          manuscript.lastUpdated || manuscript.last_updated
                        )}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewManuscript(manuscript, index)}
                        className="flex-1 px-3 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Decision
                      </button>

                      <ManuscriptActions
                        submission={manuscript}
                        navigate={navigate}
                        onContinue={handleContinueSubmission}
                        size="md"
                        variant="buttons"
                        emptyFallback={null}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (
                        pagination.currentPage >=
                        pagination.totalPages - 2
                      ) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded text-sm ${
                            pagination.currentPage === pageNum
                              ? 'bg-[--asfi-blue] text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ View Decision Modal ============ */}
      {showViewModal && (selectedManuscriptDetails || selectedManuscript) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Editorial Decision
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedManuscriptIndex !== null
                      ? `${selectedManuscriptIndex + 1} of ${pagination.totalCount}`
                      : ''}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="w-8 h-8 animate-spin text-purple-600 mr-2" />
                  <p className="text-gray-600">Loading decision details...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Manuscript Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-[--asfi-blue] mb-1">
                        {selectedManuscriptDetails?.revision_id ||
                          selectedManuscriptDetails?.id ||
                          selectedManuscript?.revision_id ||
                          selectedManuscript?.id}
                      </p>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedManuscriptDetails?.title || selectedManuscript?.title}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedManuscriptDetails?.type ||
                          selectedManuscriptDetails?.article_type ||
                          selectedManuscript?.article_type}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      <span
                        className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getDecisionColor(
                          selectedManuscriptDetails?.status || selectedManuscript?.status
                        )}`}
                      >
                        {getDecisionIcon(
                          selectedManuscriptDetails?.status || selectedManuscript?.status
                        )}
                        {getDecisionText(
                          selectedManuscriptDetails?.status || selectedManuscript?.status
                        )}
                      </span>
                      {selectedManuscriptDetails?.revisionsDue && (
                        <p className="text-xs text-gray-500 mt-2">
                          Revisions due:{' '}
                          {formatDate(selectedManuscriptDetails.revisionsDue)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Special Issue Tags */}
                  {(selectedManuscriptDetails?.is_women_in_contemporary_science == 1 ||
                    selectedManuscriptDetails?.is_kidnapping_for_ransom == 1 ||
                    selectedManuscriptDetails?.is_belispoint_academic == 1) && (
                    <div className="flex flex-wrap gap-2 pb-4 border-b border-gray-200">
                      {selectedManuscriptDetails?.is_women_in_contemporary_science == 1 && (
                        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-pink-100 text-pink-800">
                          Women in Contemporary Science in Africa
                        </span>
                      )}
                      {selectedManuscriptDetails?.is_kidnapping_for_ransom == 1 && (
                        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Kidnapping for Ransom
                        </span>
                      )}
                      {selectedManuscriptDetails?.is_belispoint_academic == 1 && (
                        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Belispoint Academic
                        </span>
                      )}
                    </div>
                  )}

                  {/* Abstract */}
                  {(selectedManuscriptDetails?.abstract || selectedManuscript?.abstract) && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h4 className="text-sm font-medium text-gray-500 mb-4">
                        Abstract
                      </h4>
                      <RichTextViewer
                        content={
                          selectedManuscriptDetails?.abstract ||
                          selectedManuscript?.abstract
                        }
                      />
                    </div>
                  )}

                  {/* Authors */}
                  {selectedManuscriptDetails?.authors_list?.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Authors
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedManuscriptDetails.authors_list.map((author, index) => {
                          const isCurrentUser =
                            author.name.includes(user?.firstName || '') &&
                            author.name.includes(user?.lastName || '');
                          return (
                            <span
                              key={index}
                              className={`px-3 py-1 text-sm rounded-full ${
                                isCurrentUser
                                  ? 'bg-[--asfi-blue] text-white font-medium'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {author.name}
                              {isCurrentUser && ' (You)'}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {selectedManuscriptDetails?.keywords?.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center">
                        <Hash className="w-4 h-4 mr-2" />
                        Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedManuscriptDetails.keywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-1">
                        Submitted Date
                      </h4>
                      <p className="text-sm text-gray-900 flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {formatDate(
                          selectedManuscriptDetails?.date_submitted ||
                            selectedManuscriptDetails?.process_start_date ||
                            selectedManuscript?.date_submitted
                        )}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-1">
                        Last Updated
                      </h4>
                      <p className="text-sm text-gray-900 flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        {formatDate(
                          selectedManuscriptDetails?.last_updated ||
                            selectedManuscriptDetails?.lastUpdated ||
                            selectedManuscript?.last_updated
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Files */}
                  {(() => {
                    const files = getManuscriptFiles(
                      selectedManuscriptDetails || selectedManuscript
                    );
                    return (
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-500 flex items-center">
                            <FileIcon className="w-4 h-4 mr-2" />
                            Manuscript Files
                          </h4>
                          <span className="text-xs text-gray-400">
                            {files.length} file{files.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {files.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">
                            No files available
                          </p>
                        ) : (
                          <ul className="divide-y divide-gray-100">
                            {files.map((file) => {
                              const Icon = getFileIcon(file.url);
                              return (
                                <li
                                  key={file.key}
                                  className="flex items-center justify-between gap-3 py-2.5"
                                >
                                  <div className="flex items-center min-w-0 flex-1">
                                    <Icon className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.label}
                                      </p>
                                      <p
                                        className="text-xs text-gray-500 truncate"
                                        title={displayFilename(file.url)}
                                      >
                                        {displayFilename(file.url)}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs font-medium text-[--asfi-purple] hover:text-[--asfi-blue] hover:underline"
                                    >
                                      Open
                                    </a>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDownloadFile(file.url, file.label)
                                      }
                                      className="p-1.5 text-gray-500 hover:text-[--asfi-purple] hover:bg-purple-50 rounded-md transition-colors"
                                      title={`Download ${file.label}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })()}

                  {/* Related Submissions */}
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Related Submissions
                      </h4>
                      <span className="text-xs text-gray-400">
                        {relatedSubmissions.length} version
                        {relatedSubmissions.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {loadingRelated ? (
                      <div className="flex justify-center py-6">
                        <Loader className="w-6 h-6 animate-spin text-purple-600" />
                      </div>
                    ) : relatedSubmissions.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-sm text-gray-500">
                          No related submissions found
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Manuscript ID
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {relatedSubmissions.map((related, index) => {
                              const relatedId = related.revision_id || related.id;
                              const currentId =
                                selectedManuscriptDetails?.revision_id ||
                                selectedManuscriptDetails?.id ||
                                selectedManuscript?.revision_id ||
                                selectedManuscript?.id;
                              const isCurrent = relatedId === currentId;

                              return (
                                <tr
                                  key={relatedId || index}
                                  className={`transition-colors ${
                                    isCurrent ? 'bg-green-50' : 'hover:bg-gray-50'
                                  }`}
                                >
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <button
                                      onClick={() =>
                                        handleSelectRelatedManuscript(related)
                                      }
                                      className="text-sm font-medium text-[--asfi-purple] hover:text-[--asfi-blue] hover:underline"
                                      title="View this version"
                                    >
                                      {relatedId}
                                    </button>
                                    {isCurrent && (
                                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Current
                                      </span>
                                    )}
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                    {formatDate(
                                      related.date_submitted ||
                                        related.process_start_date
                                    )}
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(
                                        related.status
                                      )}`}
                                    >
                                      {getStatusIcon(related.status)}
                                      {formatStatus(related.status)}
                                    </span>
                                    <StatusBadge status={related.status} />
                                  </td>

                                  <td className="px-4 py-3 whitespace-nowrap text-right">
                                    <div className="flex justify-end">
                                      <ManuscriptActions
                                        submission={related}
                                        navigate={navigate}
                                        onContinue={handleContinueSubmission}
                                        onNavigate={handleCloseModal}
                                        size="sm"
                                        variant="buttons"
                                        emptyFallback={
                                          <span className="text-xs text-gray-400">
                                            —
                                          </span>
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Editor's Decision Letter */}
                  {selectedManuscriptDetails?.decisionLetter && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <MessageSquare className="w-5 h-5 text-[--asfi-blue] mr-2" />
                          <h4 className="text-md font-semibold text-gray-900">
                            Editor's Decision Letter
                          </h4>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(selectedManuscriptDetails.decisionLetter.date)}
                        </div>
                      </div>

                      <div className="mb-4 pb-4 border-b border-purple-200">
                        <p className="text-sm font-medium text-gray-700">
                          From: {selectedManuscriptDetails.decisionLetter.from}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1 text-gray-400" />
                          {selectedManuscriptDetails.decisionLetter.recipient}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Subject: {selectedManuscriptDetails.decisionLetter.subject}
                        </p>
                      </div>

                      <div
                        className="prose prose-sm max-w-none text-gray-700 ql-editor"
                        dangerouslySetInnerHTML={{
                          __html:
                            (() => {
                              try {
                                const parsed = JSON.parse(
                                  selectedManuscriptDetails.decisionLetter.body
                                );
                                if (parsed && parsed.message) return parsed.message;
                                return selectedManuscriptDetails.decisionLetter.body;
                              } catch (e) {
                                return selectedManuscriptDetails.decisionLetter.body;
                              }
                            })() || 'No decision letter content'
                        }}
                      />
                    </div>
                  )}

                  {/* Reviewer Comments */}
                  {selectedManuscriptDetails?.reviewerComments?.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                        <Users className="w-5 h-5 mr-2 text-gray-500" />
                        Reviewer Comments (
                        {selectedManuscriptDetails.reviewerComments.length})
                      </h4>

                      <div className="space-y-4">
                        {selectedManuscriptDetails.reviewerComments.map(
                          (reviewer, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 border border-gray-200 rounded-lg p-5"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h5 className="font-medium text-gray-900">
                                    {reviewer.reviewer}
                                  </h5>
                                  <p className="text-xs text-gray-500">
                                    Recommendation: {reviewer.recommendation}
                                  </p>
                                </div>
                                <span
                                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                    reviewer.recommendation
                                      ?.toLowerCase()
                                      .includes('accept')
                                      ? 'bg-green-100 text-green-800'
                                      : reviewer.recommendation
                                          ?.toLowerCase()
                                          .includes('revision')
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : reviewer.recommendation
                                          ?.toLowerCase()
                                          .includes('reject')
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {reviewer.recommendation || 'Not specified'}
                                </span>
                              </div>

                              {reviewer.comments && (
                                <div className="mb-3">
                                  <p className="text-xs font-medium text-gray-500 mb-1">
                                    Comments to author:
                                  </p>
                                  <p className="text-sm text-gray-700 whitespace-pre-line">
                                    {reviewer.comments}
                                  </p>
                                </div>
                              )}

                              {reviewer.confidential && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <p className="text-xs font-medium text-gray-500 mb-1">
                                    Confidential comments to editor:
                                  </p>
                                  <p className="text-sm text-gray-600 italic">
                                    "{reviewer.confidential}"
                                  </p>
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* No Decision Letter Fallback */}
                  {!loadingDetails && !selectedManuscriptDetails?.decisionLetter && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                      <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                      <h4 className="text-md font-semibold text-gray-900 mb-2">
                        Decision Letter Not Available
                      </h4>
                      <p className="text-sm text-gray-600">
                        The decision letter for this manuscript is not available.
                        Please contact the editorial office for more information.
                      </p>
                    </div>
                  )}

                  {/* Revision Requirements */}
                  {(selectedManuscriptDetails?.status === 'returned_for_revision' ||
                    selectedManuscriptDetails?.status ===
                      'returned_for_correction') && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
                      <div className="flex items-center mb-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                        <h4 className="text-md font-semibold text-gray-900">
                          Revision Requirements
                        </h4>
                      </div>

                      <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 ml-6">
                        <li>Submit a point-by-point response to all reviewer comments</li>
                        <li>
                          Upload a revised manuscript with changes tracked or highlighted
                        </li>
                        <li>Ensure all formatting follows ASFIRJ guidelines</li>
                        {selectedManuscriptDetails.revisionsDue && (
                          <li>
                            Submit revisions by{' '}
                            <span className="font-medium text-yellow-700">
                              {formatDate(selectedManuscriptDetails.revisionsDue)}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Acceptance Information */}
                  {selectedManuscriptDetails?.status === 'accepted' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                      <div className="flex items-center mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <h4 className="text-md font-semibold text-gray-900">
                          Acceptance Information
                        </h4>
                      </div>

                      <p className="text-sm text-gray-700 mb-3">
                        Your manuscript has been accepted for publication. It will now
                        proceed to the production stage.
                      </p>

                      <div className="text-sm text-gray-600 space-y-1">
                        <p>• You will receive proofs within 2-3 weeks for final approval.</p>
                        <p>• Please ensure your affiliation and contact details are up to date.</p>
                        <p>• If you have any questions, contact the editorial office.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>

                <ManuscriptActions
                  submission={selectedManuscriptDetails || selectedManuscript}
                  navigate={navigate}
                  onContinue={handleContinueSubmission}
                  onNavigate={handleCloseModal}
                  size="md"
                  variant="buttons"
                  emptyFallback={null}
                />
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => handleNavigateManuscript(-1)}
              disabled={selectedManuscriptIndex === 0 || selectedManuscriptIndex === null}
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${
                selectedManuscriptIndex === 0 || selectedManuscriptIndex === null
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 cursor-pointer'
              }`}
              title="Previous Manuscript"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>

            <button
              onClick={() => handleNavigateManuscript(1)}
              disabled={
                selectedManuscriptIndex === null ||
                selectedManuscriptIndex >= pagination.totalCount - 1
              }
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${
                selectedManuscriptIndex === null ||
                selectedManuscriptIndex >= pagination.totalCount - 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 cursor-pointer'
              }`}
              title="Next Manuscript"
            >
              <ArrowRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>
      )}

      {/* Quill styles */}
      <style jsx>{`
        .ql-editor {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
            Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
        }
        .ql-editor h1 { font-size: 2em; margin: 0.67em 0; }
        .ql-editor h2 { font-size: 1.5em; margin: 0.75em 0; }
        .ql-editor h3 { font-size: 1.17em; margin: 0.83em 0; }
        .ql-editor p { margin: 0 0 1em 0; }
        .ql-editor ul, .ql-editor ol { padding-left: 1.5em; margin: 0.5em 0; }
        .ql-editor li { margin: 0.25em 0; }
        .ql-editor a { color: #7e22ce; text-decoration: underline; }
        .ql-editor strong { font-weight: 600; }
        .ql-editor em { font-style: italic; }
        .ql-editor u { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default ManuscriptsWithDecisions;